'use client';

import { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { aiModelsApi, MultipleAnglesResponse, PoseConfig } from '@/lib/ai-models-api';

// Azimuth angle positions for the circular selector
const AZIMUTH_POSITIONS = [
  { angle: 0, label: 'Front', x: 50, y: 8 },
  { angle: 45, label: 'F-R', x: 85, y: 15 },
  { angle: 90, label: 'Right', x: 92, y: 50 },
  { angle: 135, label: 'B-R', x: 85, y: 85 },
  { angle: 180, label: 'Back', x: 50, y: 92 },
  { angle: 225, label: 'B-L', x: 15, y: 85 },
  { angle: 270, label: 'Left', x: 8, y: 50 },
  { angle: 315, label: 'F-L', x: 15, y: 15 },
] as const;

const ELEVATIONS = [
  { angle: 60, label: 'High Angle', icon: '\u2B06', description: 'Bird\'s eye view' },
  { angle: 30, label: 'Elevated', icon: '\u2197', description: 'Slightly above' },
  { angle: 0, label: 'Eye Level', icon: '\u2192', description: 'Neutral view' },
  { angle: -30, label: 'Low Angle', icon: '\u2198', description: 'Heroic shot' },
] as const;

const DISTANCES = [
  { scale: 0.6, label: 'Close-up', description: 'Detail focus' },
  { scale: 1.0, label: 'Medium', description: 'Standard view' },
  { scale: 1.8, label: 'Wide', description: 'Full context' },
] as const;

// Quick preset configurations for common use cases
const PRESETS = [
  {
    name: 'Product View',
    description: 'Standard e-commerce angles',
    poses: [
      { azimuth: 0, elevation: 0, distance: 1.0 },
      { azimuth: 45, elevation: 0, distance: 1.0 },
      { azimuth: 90, elevation: 0, distance: 1.0 },
      { azimuth: 180, elevation: 0, distance: 1.0 },
    ] as PoseConfig[],
  },
  {
    name: 'Hero Shot',
    description: 'Dramatic low angles',
    poses: [
      { azimuth: 315, elevation: -30, distance: 0.6 },
      { azimuth: 45, elevation: -30, distance: 0.6 },
    ] as PoseConfig[],
  },
  {
    name: 'Overview',
    description: 'High angle context shots',
    poses: [
      { azimuth: 45, elevation: 60, distance: 1.8 },
      { azimuth: 135, elevation: 60, distance: 1.8 },
      { azimuth: 225, elevation: 60, distance: 1.8 },
      { azimuth: 315, elevation: 60, distance: 1.8 },
    ] as PoseConfig[],
  },
  {
    name: '360\u00B0 Turntable',
    description: 'All 8 horizontal angles',
    poses: AZIMUTH_POSITIONS.map(p => ({ azimuth: p.angle, elevation: 0, distance: 1.0 })) as PoseConfig[],
  },
];

export default function MultipleAnglesPage() {
  // Image state
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Camera controls
  const [azimuth, setAzimuth] = useState<number>(0);
  const [elevation, setElevation] = useState<number>(0);
  const [distance, setDistance] = useState<number>(1.0);

  // Advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loraScale, setLoraScale] = useState(0.9);
  const [guidanceScale, setGuidanceScale] = useState(4.0);
  const [steps, setSteps] = useState(40);
  const [seed, setSeed] = useState<number | null>(null);

  // Mode
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

  // Results state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MultipleAnglesResponse | null>(null);
  const [batchResults, setBatchResults] = useState<MultipleAnglesResponse[]>([]);

  // Build descriptive prompt preview
  const promptPreview = useMemo(() => {
    const azDesc = AZIMUTH_POSITIONS.find(a => a.angle === azimuth)?.label || 'Front';
    const elDesc = ELEVATIONS.find(e => e.angle === elevation)?.label || 'Eye Level';
    const distDesc = DISTANCES.find(d => d.scale === distance)?.label || 'Medium';
    return `${azDesc} view, ${elDesc}, ${distDesc} shot`;
  }, [azimuth, elevation, distance]);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setInputImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setBatchResults([]);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputImage) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === 'single') {
        const response = await aiModelsApi.multipleAnglesGenerate(inputImage, {
          azimuth,
          elevation,
          distance,
          lora_scale: loraScale,
          guidance_scale: guidanceScale,
          num_inference_steps: steps,
          seed,
        });
        setResult(response);
        setBatchResults([]);
      } else if (selectedPreset !== null) {
        const poses = PRESETS[selectedPreset].poses;
        const response = await aiModelsApi.multipleAnglesBatch(inputImage, poses, {
          lora_scale: loraScale,
          guidance_scale: guidanceScale,
          num_inference_steps: steps,
          seed,
        });
        setBatchResults(response.images);
        setResult(null);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">Multiple Angles - 3D Camera Control</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left Column: Controls (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-6">
                <h2 className="text-lg font-semibold">Camera Controls</h2>

                {/* Mode Toggle */}
                <div className="flex gap-2 p-1 bg-[var(--bg-primary)] rounded-lg">
                  <button
                    type="button"
                    onClick={() => setMode('single')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      mode === 'single'
                        ? 'bg-[var(--accent)] text-black'
                        : 'hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    Single Angle
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('batch')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      mode === 'batch'
                        ? 'bg-[var(--accent)] text-black'
                        : 'hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    Batch Preset
                  </button>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Input Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-40 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setInputImage(null);
                            setImagePreview(null);
                            setResult(null);
                            setBatchResults([]);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {mode === 'single' ? (
                  <>
                    {/* Azimuth Selector - Circular Compass */}
                    <div>
                      <label className="block text-sm font-medium mb-3">Azimuth (Horizontal Rotation)</label>
                      <div className="relative w-48 h-48 mx-auto">
                        {/* Compass circle */}
                        <div className="absolute inset-0 rounded-full border-2 border-[var(--border-subtle)] bg-[var(--bg-primary)]" />

                        {/* Center indicator */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 rounded-full bg-[var(--accent)]" />
                        </div>

                        {/* Direction line */}
                        <div
                          className="absolute top-1/2 left-1/2 w-[40%] h-0.5 bg-[var(--accent)] origin-left transition-transform duration-300"
                          style={{ transform: `rotate(${azimuth - 90}deg)` }}
                        />

                        {/* Angle buttons */}
                        {AZIMUTH_POSITIONS.map((pos) => (
                          <button
                            key={pos.angle}
                            type="button"
                            onClick={() => setAzimuth(pos.angle)}
                            className={`absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full text-xs font-medium transition-all ${
                              azimuth === pos.angle
                                ? 'bg-[var(--accent)] text-black scale-110'
                                : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]'
                            }`}
                            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                          >
                            {pos.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-center text-sm text-[var(--text-muted)] mt-2">
                        {AZIMUTH_POSITIONS.find(a => a.angle === azimuth)?.label} View ({azimuth}\u00B0)
                      </p>
                    </div>

                    {/* Elevation Selector - Vertical Buttons */}
                    <div>
                      <label className="block text-sm font-medium mb-3">Elevation (Vertical Angle)</label>
                      <div className="grid grid-cols-2 gap-2">
                        {ELEVATIONS.map((elev) => (
                          <button
                            key={elev.angle}
                            type="button"
                            onClick={() => setElevation(elev.angle)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left ${
                              elevation === elev.angle
                                ? 'bg-[var(--accent)] text-black'
                                : 'bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)]'
                            }`}
                          >
                            <span className="text-lg">{elev.icon}</span>
                            <div>
                              <div className="text-sm font-medium">{elev.label}</div>
                              <div className="text-xs opacity-60">{elev.angle}\u00B0</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Distance Selector */}
                    <div>
                      <label className="block text-sm font-medium mb-3">Distance (Zoom)</label>
                      <div className="flex gap-2">
                        {DISTANCES.map((dist) => (
                          <button
                            key={dist.scale}
                            type="button"
                            onClick={() => setDistance(dist.scale)}
                            className={`flex-1 flex flex-col items-center gap-1 py-3 px-3 rounded-lg transition-all ${
                              distance === dist.scale
                                ? 'bg-[var(--accent)] text-black'
                                : 'bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)]'
                            }`}
                          >
                            <span className="text-sm font-medium">{dist.label}</span>
                            <span className="text-xs opacity-60">{dist.scale}x</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Prompt Preview */}
                    <div className="p-3 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase mb-1">Camera Prompt Preview</p>
                      <p className="text-sm font-mono">{promptPreview}</p>
                    </div>
                  </>
                ) : (
                  /* Batch Preset Selector */
                  <div>
                    <label className="block text-sm font-medium mb-3">Select Preset</label>
                    <div className="space-y-2">
                      {PRESETS.map((preset, idx) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => setSelectedPreset(idx)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all text-left ${
                            selectedPreset === idx
                              ? 'bg-[var(--accent)] text-black'
                              : 'bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)]'
                          }`}
                        >
                          <div>
                            <div className="font-medium">{preset.name}</div>
                            <div className="text-xs opacity-60">{preset.description}</div>
                          </div>
                          <div className={`text-sm ${selectedPreset === idx ? 'text-black/60' : 'text-[var(--text-muted)]'}`}>
                            {preset.poses.length} angles
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Advanced Settings Toggle */}
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <span>Advanced Settings</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Advanced Settings Panel */}
                {showAdvanced && (
                  <div className="space-y-4 pt-2 border-t border-[var(--border-subtle)]">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        LoRA Strength: {loraScale.toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="1.2"
                        step="0.05"
                        value={loraScale}
                        onChange={(e) => setLoraScale(parseFloat(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        Higher = stronger angle transformation
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Guidance Scale: {guidanceScale.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        step="0.5"
                        value={guidanceScale}
                        onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Steps: {steps}
                      </label>
                      <input
                        type="range"
                        min="20"
                        max="60"
                        step="5"
                        value={steps}
                        onChange={(e) => setSteps(parseInt(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Seed (Optional)</label>
                      <input
                        type="number"
                        value={seed ?? ''}
                        onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                        placeholder="Random"
                      />
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!inputImage || loading || (mode === 'batch' && selectedPreset === null)}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : mode === 'single' ? 'Generate Angle' : 'Generate Batch'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right Column: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">
                  {mode === 'single' ? 'Generated Angle' : 'Generated Angles'}
                </h2>

                {!result && batchResults.length === 0 && !loading && (
                  <div className="flex flex-col items-center justify-center py-32 text-center">
                    <svg className="w-20 h-20 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and select a camera angle</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Best results with product photos and 3D-style images
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-32">
                    <div className="w-12 h-12 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating new angle...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">This may take 30-60 seconds</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Side-by-side comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Original</p>
                        {imagePreview && (
                          <img
                            src={imagePreview}
                            alt="Original"
                            className="w-full rounded-lg bg-[var(--bg-primary)]"
                          />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Generated</p>
                        <img
                          src={result.image}
                          alt="Generated"
                          className="w-full rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Download Button */}
                    <a
                      href={result.image}
                      download={`angle_${result.azimuth}_${result.elevation}_${result.distance}.png`}
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Image
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Azimuth</p>
                        <p className="text-sm font-semibold">{result.azimuth_descriptor}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Elevation</p>
                        <p className="text-sm font-semibold">{result.elevation_descriptor}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Distance</p>
                        <p className="text-sm font-semibold">{result.distance_descriptor}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-sm font-semibold">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Full prompt */}
                    <div className="p-3 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase mb-1">Camera Prompt Used</p>
                      <code className="text-sm font-mono">{result.prompt}</code>
                    </div>
                  </div>
                )}

                {batchResults.length > 0 && (
                  <div className="space-y-4">
                    {/* Grid of results */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {batchResults.map((res, idx) => (
                        <div key={idx} className="space-y-2">
                          <img
                            src={res.image}
                            alt={`Generated ${idx + 1}`}
                            className="w-full rounded-lg"
                          />
                          <div className="text-xs text-center text-[var(--text-muted)]">
                            {res.azimuth_descriptor}
                          </div>
                          <a
                            href={res.image}
                            download={`angle_${res.azimuth}_${res.elevation}_${res.distance}.png`}
                            className="block w-full py-1.5 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-xs text-center rounded-lg transition-colors"
                          >
                            Download
                          </a>
                        </div>
                      ))}
                    </div>

                    {/* Total time */}
                    <div className="p-3 bg-[var(--bg-primary)] rounded-lg text-center">
                      <p className="text-sm text-[var(--text-muted)]">
                        Generated {batchResults.length} images in{' '}
                        {(batchResults.reduce((sum, r) => sum + r.inference_time_ms, 0) / 1000).toFixed(1)}s
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
